export interface VisibilitySource {
  isVisible(): boolean;
  subscribe(listener: () => void): () => void;
}

export const browserVisibilitySource: VisibilitySource = {
  isVisible: () => document.visibilityState === 'visible',
  subscribe: (listener) => {
    document.addEventListener('visibilitychange', listener);
    return () => document.removeEventListener('visibilitychange', listener);
  },
};

export class ReadStateController {
  private readonly sendReceipt: (
    roomId: string,
    eventId: string,
  ) => Promise<void>;
  private readonly visibility: VisibilitySource;
  private readonly unsubscribeVisibility: () => void;
  private selectedRoomId: string | null = null;
  private latestVisible: { roomId: string; eventId: string } | null = null;
  private readonly lastSent = new Map<string, string>();
  private readonly inFlight = new Set<string>();
  private disposed = false;

  constructor(
    sendReceipt: (roomId: string, eventId: string) => Promise<void>,
    visibility: VisibilitySource = browserVisibilitySource,
  ) {
    this.sendReceipt = sendReceipt;
    this.visibility = visibility;
    this.unsubscribeVisibility = visibility.subscribe(() => void this.flush());
  }

  selectRoom = (roomId: string | null) => {
    if (this.disposed) return;
    this.selectedRoomId = roomId;
    void this.flush();
  };

  markLatestVisible = (roomId: string, eventId: string) => {
    if (this.disposed) return;
    this.latestVisible = { roomId, eventId };
    void this.flush();
  };

  clearLatestVisible = (roomId: string, eventId: string) => {
    if (this.disposed) return;
    if (
      this.latestVisible?.roomId === roomId &&
      this.latestVisible.eventId === eventId
    )
      this.latestVisible = null;
  };

  dispose = () => {
    if (this.disposed) return;
    this.disposed = true;
    this.selectedRoomId = null;
    this.latestVisible = null;
    this.unsubscribeVisibility();
  };

  private flush = async () => {
    await Promise.resolve();
    if (this.disposed) return;
    const latest = this.latestVisible;
    if (!latest || this.selectedRoomId !== latest.roomId) return;
    if (!this.visibility.isVisible()) return;
    if (this.lastSent.get(latest.roomId) === latest.eventId) return;
    const key = `${latest.roomId}\u0000${latest.eventId}`;
    if (this.inFlight.has(key)) return;

    this.inFlight.add(key);
    try {
      await this.sendReceipt(latest.roomId, latest.eventId);
      this.lastSent.set(latest.roomId, latest.eventId);
    } catch {
      return;
    } finally {
      this.inFlight.delete(key);
    }
  };
}

interface IntersectionEntry {
  isIntersecting: boolean;
}

interface LatestEventObserver {
  observe(element: Element): void;
  disconnect(): void;
}

type LatestEventObserverFactory = (
  listener: (entries: IntersectionEntry[]) => void,
) => LatestEventObserver;

const browserObserverFactory: LatestEventObserverFactory = (listener) =>
  new IntersectionObserver((entries) => listener(entries));

export const createLatestEventObserver = (
  element: Element,
  onVisible: () => void,
  onHidden: () => void,
  observerFactory: LatestEventObserverFactory = browserObserverFactory,
) => {
  const observer = observerFactory((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) onVisible();
    else onHidden();
  });
  observer.observe(element);
  return () => observer.disconnect();
};
